import { Middleware } from 'openapi-fetch'

import env from '~/env'
import type { useStore as UseStore } from '~/store/store'
import { isNotNullish } from '~/utils/guards'
import { expireSession } from '~/utils/session'

// load `useStore` lazily because of circular imports
let useStore: typeof UseStore | undefined = undefined
async function getStore(): Promise<typeof UseStore> {
    if (useStore === undefined) {
        useStore = (await import('~/store/store')).useStore
    }
    return useStore
}

const IAP_RELOAD_AT_KEY = 'dips-iap-reload-at'
const IAP_RELOAD_COOLDOWN_MS = 60_000

// IAP's own 401 is an HTML page; mock-dips rejects a token with JSON.
function isIapRejection(response: Response) {
    return env.VITE_DIPS_BEHIND_IAP && !response.headers.get('Content-Type')?.includes('application/json')
}

// Only a full page load of an IAP-protected path can (re)establish the IAP session cookie. Rate-limited so a
// 401 that survives the reload surfaces as a failed request instead of a reload loop.
function reloadForIapSignIn() {
    try {
        const lastReloadAt = Number(sessionStorage.getItem(IAP_RELOAD_AT_KEY))
        if (Date.now() - lastReloadAt < IAP_RELOAD_COOLDOWN_MS) return
        sessionStorage.setItem(IAP_RELOAD_AT_KEY, String(Date.now()))
    } catch {
        return
    }
    window.location.reload()
}

export const dipsSessionRevocationMiddleware: Middleware = {
    async onResponse(response) {
        if (response.status === 401 && isIapRejection(response)) {
            // The DIPS token is fine; revoking it would log the user out of Hero over an IAP session expiry.
            reloadForIapSignIn()
        } else if (response.status === 401) {
            const hasDipsCredentials = (await getStore()).getState().user.dipsApiAccessToken !== null
            if (hasDipsCredentials) {
                const diApiRevocationURL = new URL(env.VITE_API_BASE_URL + '/oauth/revocation').toString()

                try {
                    await fetch(diApiRevocationURL, {
                        mode: 'cors',
                        credentials: 'include',
                    })
                } catch (err) {
                    console.error(err)
                } finally {
                    window.location.reload()
                }
            }
        }
        return response
    },
}

export const apiRefreshingMiddleware: Middleware = {
    async onResponse(response, _options, request) {
        // Token renewal owns its expiry/retry policy rather than treating every 401 as a page reload.
        if (new URL(request.url).pathname.endsWith('/oauth/token')) return response
        if (response.status === 401 && !env.VITE_FF_REFRESH_TOKEN) {
            const isAuthenticated = isNotNullish((await getStore()).getState().user.apiAccessToken)
            if (isAuthenticated) {
                expireSession()
            }
            return response
        }
    },
}
